import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Building2, Users, AlertTriangle, ClipboardCheck, ListChecks, ShieldAlert, Download } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";

const RISK_COLORS = {
  basso: "bg-emerald-50 text-emerald-700",
  moderato: "bg-amber-50 text-amber-800",
  significativo: "bg-orange-50 text-orange-700",
  alto: "bg-red-50 text-red-700",
};

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="shadow-none" data-testid={testid}>
      <CardContent className="space-y-2 p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-900">
          <Icon className="h-4 w-4" /> {label}
        </div>
        <div className="text-3xl font-bold text-slate-950">{value}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function CrossCompanyAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sector, setSector] = useState("");

  const load = async () => {
    setError("");
    try {
      const q = sector ? `?sector=${encodeURIComponent(sector)}` : "";
      const r = await apiRequest(`/analytics/cross-company${q}`);
      setData(r);
    } catch (err) {
      setError(err.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sector]);

  if (!data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8" data-testid="analytics-loading">
        {error ? <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert> : "Caricamento KPI..."}
      </div>
    );
  }

  const totals = data.totals || {};
  const sortable = [...(data.companies || [])].sort((a, b) => b.completion_percentage - a.completion_percentage);

  return (
    <div className="space-y-6" data-testid="cross-company-analytics-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Analytics multi-azienda</p>
            <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Dashboard consulente</h2>
            <p className="mt-2 text-slate-600">
              KPI cross-company e benchmark di settore per il portafoglio di aziende che gestisci.
              Scope corrente: <strong>{data.user_scope}</strong>.
            </p>
          </div>
          <Button
            data-testid="benchmark-pdf-button"
            className="bg-blue-900 text-white hover:bg-blue-800"
            onClick={async () => {
              try {
                const q = sector ? `?sector=${encodeURIComponent(sector)}` : "";
                await downloadFile(`/analytics/benchmark-pdf${q}`, `benchmark_settore.pdf`);
                toast.success("Benchmark PDF scaricato");
              } catch (err) {
                toast.error(`Errore download: ${err.message}`);
              }
            }}
          >
            <Download className="mr-2 h-4 w-4" /> Benchmark PDF
          </Button>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard icon={Building2} label="Aziende" value={totals.companies || 0} testid="metric-companies" />
        <MetricCard icon={Users} label="Lavoratori totali" value={totals.workers || 0} testid="metric-workers" />
        <MetricCard icon={ClipboardCheck} label="Completamento medio" value={`${totals.avg_completion || 0}%`} testid="metric-avg-completion" />
        <MetricCard icon={ShieldAlert} label="Rischi alti/sign." value={totals.total_high_risks || 0} testid="metric-high-risks" />
      </div>

      <Card className="shadow-none">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg"><BarChart3 className="h-5 w-5" /> Portafoglio aziende</CardTitle>
          <input
            placeholder="Filtra per settore ATECO/sector..."
            className="w-72 rounded-md border border-slate-200 px-3 py-1 text-sm"
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            data-testid="analytics-sector-filter"
          />
        </CardHeader>
        <CardContent className="space-y-2 p-3">
          {sortable.length === 0 && <p className="rounded-md border border-dashed p-6 text-center text-sm text-slate-500" data-testid="analytics-empty">Nessuna azienda corrisponde al filtro.</p>}
          {sortable.map((c) => (
            <Link
              key={c.id}
              to={`/aziende/${c.id}`}
              className="block rounded-md border border-slate-200 p-3 transition hover:bg-slate-50"
              data-testid={`analytics-company-row-${c.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="font-semibold text-slate-950">{c.business_name}</div>
                  <div className="text-xs text-slate-500">
                    ATECO {c.ateco_code || "—"} · {c.workers} lavoratori · {c.sites} sedi
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge className="bg-blue-50 text-blue-900">Completamento {c.completion_percentage}%</Badge>
                  {["basso", "moderato", "significativo", "alto"].map((lev) =>
                    c.risks[lev] > 0 ? (
                      <Badge key={lev} className={RISK_COLORS[lev]}>{lev}: {c.risks[lev]}</Badge>
                    ) : null,
                  )}
                  <Badge className="bg-emerald-50 text-emerald-700">Rev. firmate: {c.revisions.signed}</Badge>
                  {c.revisions.pending > 0 && <Badge className="bg-amber-50 text-amber-800">Pendenti: {c.revisions.pending}</Badge>}
                  {c.improvement_plan.overdue > 0 && (
                    <Badge className="bg-red-50 text-red-700">Azioni in ritardo: {c.improvement_plan.overdue}</Badge>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="sector-benchmark-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><ListChecks className="h-5 w-5" /> Benchmark settore (ATECO)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-auto p-3">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-2 text-left">ATECO</th>
                <th className="px-2 py-2 text-right">Aziende</th>
                <th className="px-2 py-2 text-right">Compl. medio %</th>
                <th className="px-2 py-2 text-right">Rischi alti/azienda</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(data.sector_benchmark || {}).sort((a, b) => b.companies - a.companies).map((s) => (
                <tr key={s.ateco_code} className="border-t border-slate-100" data-testid={`sector-row-${s.ateco_code}`}>
                  <td className="px-2 py-2 font-semibold">{s.ateco_code}</td>
                  <td className="px-2 py-2 text-right">{s.companies}</td>
                  <td className="px-2 py-2 text-right">{s.avg_completion}%</td>
                  <td className="px-2 py-2 text-right">{s.avg_high_risks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button variant="outline" onClick={load} data-testid="analytics-refresh-button">
          Aggiorna
        </Button>
      </div>
    </div>
  );
}
